import { api, writeDB } from './store';
import { Company } from './company';
import { history } from './history';
import { contractPath } from '../routes';
import { Product } from './product';
import { Shop } from './shop';
import { GetParams, PaginatedLoaderResponse, paginatedLoaderResponseFrom } from './data';
import { BasePermissions } from './permission';
import { PaginatedResponse } from './api';

/* INTERFACES */
export interface ContractInfo {
  name: string;
  archived: boolean;
}

export interface Contract extends ContractInfo {
  id: string;
  reference: string;
  contractAgreements: ResponseAgreement[];
  products: Product[];
  companies: Company[];
  createdAt: Date;
  status: string;
  $permissions: BasePermissions[];
}

export interface RequestAgreement {
  name: string;
  shopIds: string[];
  contractAgreementLines: ContractLine[];
}

export interface ResponseAgreement {
  id: string;
  name: string;
  shops: Shop[];
  createdAt: Date;
  invalidatedAt: Date;
  contractAgreementLines?: ContractLine[];
  status: 'active' | 'inactive';
}

export interface ContractLine {
  id?: string;
  companyId: string;
  postTax: boolean;
  vatOverride: number | null;
  fee: boolean;
  amount: number;
  contractType: string;
  description?: string | null;
}

/* CONTRACTS CREATE */
export async function createContract(contractInfo: ContractInfo): Promise<void> {
  const response = await api.createContract(contractInfo);
  writeDB.table('contracts').insert(response.data);
  history.push(contractPath(response.data.id));
}

/* CONTRACTS UPDATE */
export async function updateContract(contractId: string, contractInfo: ContractInfo): Promise<void> {
  const response = await api.updateContract({
    contractId,
    ...contractInfo,
  });

  // TODO: Update api model to match local interface
  const data = response.data as unknown as Contract;
  writeDB.table('contracts').update(contractId, data);
}

/* CONTRACTS READ */
export async function getRawContracts({ page, perPage, term }: GetParams): Promise<PaginatedResponse<Contract[]>> {
  const response = await api.indexContracts({
    page: (page || 0) + 1,
    perPage,
    term,
  });
  return response as PaginatedResponse<Contract[]>;
}

export async function getContracts({
  term,
  page,
  perPage,
  sortBy,
  sortDir,
  archived,
}: {
  term?: string;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDir?: string;
  archived?: 'true' | 'false' | 'all';
}): Promise<PaginatedLoaderResponse> {
  const response = await api.indexContracts({
    page: (page || 0) + 1,
    perPage,
    term,
    sortBy,
    sortDir,
    archived,
  });
  writeDB.table('contracts').upsert(response.data);
  return paginatedLoaderResponseFrom(response);
}

export async function getRawContract(contractId: string): Promise<PaginatedResponse<Contract>> {
  const response = await api.showContract({ contractId });
  return response as unknown as PaginatedResponse<Contract>;
}

export async function getContract(contractId: string): Promise<Contract> {
  const response = await api.showContract({ contractId });
  const contract = response.data as unknown as Contract;
  writeDB.table('contracts').upsert(contract);
  return contract;
}

/* CONTRACTS UPDATE */
export async function addAgreementToContract(contractId: string, agreement: RequestAgreement): Promise<void> {
  const contract = writeDB.table('contracts').find(contractId);
  if (contract) {
    await api.createContractAgreement({
      contractId,
      ...agreement,
    });

    // to handle embedded company
    const updatedContract = (
      await api.showContract({
        contractId,
      })
    ).data as Contract;

    writeDB.table('contracts').update(contractId, updatedContract);
  }
}

export function vatRateMatchingContractVatRate(contract?: Contract, vatDE?: number): boolean {
  if (!contract || !vatDE) {
    return true;
  }

  const activeAgreements = contract.contractAgreements.filter((aggreement) => !aggreement.invalidatedAt);

  if (activeAgreements.length && activeAgreements[0].contractAgreementLines !== undefined) {
    return activeAgreements[0].contractAgreementLines.every((line) => line.vatOverride === vatDE || !line.vatOverride);
  } else {
    return true;
  }
}
